import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useWines, Wine } from "./hooks/useWines";
import { useBrand } from "./hooks/useBrand";
import { AppRoutes, ROUTES } from "./routes";
import { useAuth } from "./contexts/AuthContext";
import type { Brand } from "./pages/Carte/carte.types";

const MAISON_STORAGE_PREFIX = "vinai-maison";

interface AppRouterProps {
  lang: string;
}

export function AppRouter({ lang }: AppRouterProps) {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { wines: rawWines, fetchWines } = useWines();
  const { brand: rawBrand, fetchBrand, getAdminEmail } = useBrand();

  const [wines, setWines] = useState<Wine[]>([]);
  const [maison, setMaison] = useState("");
  const [updated, setUpdated] = useState(
    new Date().toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
  );
  const [brand, setBrand] = useState<Brand>({ logo: null, bgColor: "#0A0908", textColor: "#F0EDE8", cardColor: "#141210", feeder: { accountType: "domaine", domaine: { philosophie: [], vinification: [], elevage: [], style: [] }, resto: { menu: "" } } });
  const [adminEmail, setAdminEmailState] = useState("");

  useEffect(() => {
    if (loading) return;
    if (user) {
      fetchWines();
    } else {
      setWines([]);
    }
    fetchBrand();
    getAdminEmail().then(setAdminEmailState);
  }, [loading, user, fetchWines, fetchBrand, getAdminEmail]);

  useEffect(() => {
    setWines(
      rawWines.map((w: any) => ({
        ...w,
        gen: false,
        visible: typeof w.visible === "boolean" ? w.visible : true,
        highlighted: typeof w.highlighted === "boolean" ? w.highlighted : false,
      }))
    )
  }, [rawWines]);

  useEffect(() => {
    if (rawBrand) setBrand(rawBrand);
  }, [rawBrand]);

  useEffect(() => {
    if (!user) {
      setMaison("");
      return;
    }

    const savedMaison = window.localStorage.getItem(`${MAISON_STORAGE_PREFIX}:${user.uid}`) || "";
    setMaison((currentMaison) => (currentMaison === savedMaison ? currentMaison : savedMaison));
  }, [user]);

  useEffect(() => {
    if (loading || !user) {
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      window.localStorage.setItem(`${MAISON_STORAGE_PREFIX}:${user.uid}`, maison);
    }, 400);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [loading, maison, user]);

  return (
    <AppRoutes
      lang={lang}
      wines={wines}
      setWines={setWines}
      maison={maison}
      setMaison={setMaison}
      updated={updated}
      setUpdated={setUpdated}
      brand={brand}
      setBrand={setBrand}
      onPublic={() => navigate(ROUTES.CARTE_PUBLIC)}
      onBack={() => navigate(ROUTES.CARTE)}
      adminEmail={adminEmail}
      setAdminEmail={setAdminEmailState}
    />
  );
}
